import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';

import { Observable, of } from 'rxjs';
import { map, take, takeWhile, withLatestFrom } from 'rxjs/operators';

import { G3FormValidation, G3FormValidationType, G3ModalService } from '@cgm/g3-component-lib';

import { CustomerCreateFacade } from '../../store/customer-create.facade';
import { formValue } from '@g3p/shared/helpers/helpers';
import { CustomValidatorRegex } from '@g3p/shared/validators/validators-regex';
import {
  AccountFormDef,
  CustomerUserDef,
  UserCustomerUsageDto,
  UserUsage
} from '@g3p/customer/interfaces/customer-def.interface';
import { CustomerCreateService } from '@g3p/customer/components/customer-create-modal/services/customer-create.service';
import {
  AccountUsageModalComponent
} from '@g3p/customer/components/customer-create-modal/components/account-usage-modal/account-usage-modal.component';

@Component({
  selector: 'g3p-account-step',
  templateUrl: './account-step.component.html',
  styleUrls: ['./account-step.component.scss']
})
export class AccountStepComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() accountGroupArray: FormArray;
  @Input() formValidation: G3FormValidation[];
  @Input() formErrors: boolean[];
  @Output() validChange = new EventEmitter<boolean>();

  dataAutomation = 'CUSTOMER-CREATE-Account';

  currentTabIndex = 0;
  editMode = false;
  customer: CustomerUserDef;
  showPassword: boolean[] = [];

  private alive = true;

  constructor(
    private formBuilder: FormBuilder,
    private facade: CustomerCreateFacade,
    private service: CustomerCreateService,
    private modalService: G3ModalService,
    private translateService: TranslateService,
  ) { }

  get forms(): FormGroup[] {
    return this.accountGroupArray.controls as FormGroup[];
  }

  ngOnInit(): void {
    this.currentTabIndex = 0;
    this.notifyEditMode();
    this.updateFormState();
    this.updateFormData();
  }

  ngAfterViewInit(): void {
    if (this.forms.length === 0) {
      this.onAddAccountForm('', true, false);
    }
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  notifyEditMode(): void {
    this.facade
      .getEditMode$()
      .pipe(takeWhile(() => this.alive))
      .subscribe((isEditMode: boolean) => {
        this.editMode = isEditMode;
      });
  }

  updateFormState(): void {
    this.accountGroupArray.valueChanges
      .pipe(
        takeWhile(() => this.alive),
        withLatestFrom(this.facade.getCreateDialogOpened$())
      )
      .subscribe(([_, isOpened]: [FormGroup, boolean]) => {
        if (isOpened) {
          this.validChange.emit(this.accountGroupArray.valid);
          this.facade.updateAccountForm(this.buildStepFormDef());
        }
      });
  }

  updateFormData(): void {
    this.facade
      .selectAccountStep$()
      .pipe(take(1))
      .subscribe((form: AccountFormDef[]) => this.patchFormValue(form));
  }

  checkFormStatus(index: number): void {
    if (this.forms.length > 0) {
      this.formErrors[index] = !this.formValidation[index].isFormValid(this.formAt(index));
    }
    this.formAt(index).markAllAsTouched();
  }

  expandItem(expanded: boolean, index: number): void {
    this.currentTabIndex = index;
    this.formAt(index).get('expanded').setValue(expanded);
    !expanded ? this.checkFormStatus(index): null;
  }

  buildStepFormDef(): AccountFormDef[] {
    return this.forms.map(form => formValue(form));
  }

  patchFormValue(values: AccountFormDef[]): void {
    this.addMissingPanel(values);
    this.removeExtraPanel(values);
    this.forms.forEach((form, idx) => {
      form.patchValue( values[idx] );
      if (!this.editMode) {
        form.get('id').value && form.disable();
      }
    });
  }

  addAccountFormClick(): void {
    if (this.currentTabIndex < 0 || (this.accountGroupArray.length === 0) || (this.formAt(this.currentTabIndex).valid) || (this.formAt(this.currentTabIndex).disabled)) {
      this.onAddAccountForm('',false, false);
    } else {
      this.formValidation[this.currentTabIndex].post();
    }
  }

  onAddAccountForm( description: string, deleteDisabled: boolean, alreadyCreated: boolean): FormGroup {
    this.formErrors.push(false);
    this.showPassword.push(false);
    for (let i = 0; i < this.forms.length; i++) {
      this.formAt(i).get('expanded').setValue(false);
    }
    this.formValidation.push(new G3FormValidation({
      [G3FormValidationType.Post]: ['required'],
      [G3FormValidationType.Blur]: ['min', 'max', 'minlength', 'maxlength']
    }));
    this.formValidation[this.formValidation.length-1].cancelPost();
    const form = this.formBuilder.group({
      username: new FormControl(description, {
        validators: Validators.max(80),
        asyncValidators: this.asyncUsage('username', alreadyCreated),
        updateOn: 'blur'
      }),
      firstName: new FormControl(''),
      lastName: new FormControl(''),
      email: new FormControl('', {
        validators: Validators.pattern(CustomValidatorRegex.EMAIL_REGEXP),
        asyncValidators: this.asyncUsage('email', alreadyCreated),
        updateOn: 'blur'
      }),
      endDate: new FormControl(''),
      initialPassword: new FormControl('', Validators.minLength(8)),
      id: new FormControl(null),
      expanded: new FormControl(true)
    });
    this.accountGroupArray.push(form);
    return form;
  }

  private asyncUsage(fieldName: string, noCheck: boolean): AsyncValidatorFn {
    return (function(control: AbstractControl): Observable<ValidationErrors> {
      const value = control.value;
      const usageCriteria = {
        [fieldName]: value
      };

      if (!value || noCheck) {
        return of(null);
      }

      if (fieldName === 'username') {
        let count = 0;
        this.forms.forEach((form) => {
          if (!form.get('id').value && form.get('username').value.toLowerCase() === value.toLowerCase()) {
            count++;
          }
        });
        if (count > 1) {
          return of({ usernameExists: true });
        }
      }

      if (fieldName === 'email') {
        let count = 0;
        this.forms.forEach((form) => {
          if (!form.get('id').value && form.get('email').value.toLowerCase() === value.toLowerCase()) {
            count++;
          }
        });
        if (count > 1) {
          return of({ emailExists: true });
        }
      }

      return this.service.isUserInUse$(usageCriteria).pipe(
        map((inUsage: UserUsage) => this.checkUsageValue(inUsage, fieldName)(control))
      );
    }).bind(this);
  }

  remove(index: number): void {
    this.currentTabIndex = this.currentTabIndex >= index ? this.currentTabIndex - 1 : this.currentTabIndex;
    this.accountGroupArray.removeAt(index);
  }

  formAt( index: number ): FormGroup {
    return this.accountGroupArray.at(index) as FormGroup;
  }

  resetPassword( index: number ):void {
    this.showPassword[index] = !this.showPassword[index];
    if (!this.showPassword[index]) {
      this.formAt(this.currentTabIndex).get('initialPassword').removeValidators(Validators.required);
      this.formAt(this.currentTabIndex).get('initialPassword').setValue(null);
      this.formAt(this.currentTabIndex).get('initialPassword').updateValueAndValidity();
      this.formAt(this.currentTabIndex).get('initialPassword').setErrors(null);
      this.formErrors[index] = false;
      this.validChange.emit(true);
    }
  }

  isUpdateMode( index: number): boolean {
    return this.formAt(index)?.get('id').value;
  }

  selectInUseValue(field: string): void {
    const id = this.formAt(this.currentTabIndex).get(field).errors.inUse.userId;

    this.service.getUserById$(id)
      .pipe(take(1))
      .subscribe((customer: CustomerUserDef) => {
        this.customer = customer;
        this.accountGroupArray.removeAt(this.currentTabIndex);
        this.formErrors.splice(this.currentTabIndex, 1);
        this.onAddAccountForm('',false, true);
        this.formAt(this.currentTabIndex).patchValue(this.customer);
      });
  }

  openModal(customers: UserCustomerUsageDto[], fieldName: string): void {
    const dialogRef = this.modalService.openRegular(AccountUsageModalComponent,{ remWidth: 35 }, {
      linkedCustomers: this.getItemDescriptions(customers),
      content: this.getContentLabels(fieldName),
      returnValue: true
    });

    dialogRef.afterClosed().subscribe(result => {
      result ? this.selectInUseValue(fieldName) : null
    })
  }

  getItemDescriptions(customers: UserCustomerUsageDto[]): string[] {
    return customers.map(customer => `${customer.name}, ${customer.city}, ${customer.street}`);
  }

  getContentLabels(fieldName: string) {
    return fieldName === 'username' ?
      {
        title: this.translateService.instant('content.user-name-already-in-use'),
        description: this.translateService.instant('content.username-already-exists-and-has-access-to-cashbook-of-the-following-customers')
      } :
      {
        title: this.translateService.instant('content.email-already-in-use'),
        description: this.translateService.instant('content.user-with-this-email-already-exists-and-has-access-to-cashbook-of-the-following-customers')
      };
  }

  resetField(fieldName: string): void {
    this.formAt(this.currentTabIndex).get(fieldName).reset();
  }

  isDeactivated(index): boolean {
    return this.isUpdateMode( index );
  }

  onFocusField(fieldName: string): void {
    this.formAt(this.currentTabIndex).get(fieldName).setErrors(null);

  }
  private addMissingPanel(values: AccountFormDef[]): void {
    for (let i = this.forms.length; i < values.length; i++) {
      this.onAddAccountForm(values[i].username, i===0 || !!values[i].id, !!values[i].id);
    }
  }

  private removeExtraPanel(values: AccountFormDef[]): void {
    const extraForms = this.accountGroupArray.length - values.length;
    for (let i = 0; i < extraForms; i++) {
      this.remove(this.accountGroupArray.length - 1);
    }
  }

  private checkUsageValue(inUse: UserUsage, fieldName: string): ValidatorFn {
    return (_: AbstractControl): ValidationErrors | null => {
      if (inUse.userId) {
        this.openModal(inUse.linkedCustomers, fieldName);
        return { inUse };
      }
      return null;
    };
  }
}
